import { FC, useEffect, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useCookies } from "react-cookie";
import {
  GoogleOAuthProvider,
  GoogleLogin,
  CredentialResponse,
} from "@react-oauth/google";

import { setIsLoggedIn, setSessionToken, setUserData } from "../store/user";
import classes from "./Login.module.css";
import Button from "./UI/Button";
import { getLang, i18n } from "../i18n";

interface Props {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const Login: FC<Props> = ({ setIsLoading }) => {
  const lang = getLang();
  const t = i18n.login[lang];

  const [inviteCode, setInviteCode] = useState("");
  const [email, setEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const [cookies, setCookie] = useCookies(["access_token"]);
  const API_URL = process.env.REACT_APP_API_URL;

  const dispatch = useDispatch();

  // --- Universal Invite Code Reader ---
  const getInviteCode = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("code") ||
      localStorage.getItem("inviteCode") ||
      inviteCode ||
      ""
    );
  };

  // Persist invite code (URL → state → localStorage)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    if (code) {
      setInviteCode(code);
      localStorage.setItem("inviteCode", code);
    }
  }, []);

  // Magic link request
  const handleRequestMagicLink = async () => {
    if (!email) return alert("Please enter your email");

    const codeToPass = getInviteCode();

    setIsLoading(true);
    try {
      await axios.post(
        `${API_URL}/users/magiclink/request`,
        { email, code: codeToPass },
        { withCredentials: true }
      );
      setMagicLinkSent(true);
    } catch (error: any) {
      console.log("Magic link request error:", error.response || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Safe JWT decode for Google response
  const decodeJwtResponse = (token: string) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  };

  // Google Login Handler (100% reliable)
  const handleCredentialResponse = (credential: CredentialResponse) => {
    if (!credential.credential) {
      console.log("Google credential missing");
      return;
    }

    let data = null;
    try {
      data = decodeJwtResponse(credential.credential);
    } catch (e) {
      console.error("Google JWT decode failed:", e);
      return;
    }

    const codeToPass = getInviteCode();

    setIsLoading(true);

    axios
      .post(
        `${API_URL}/users/enter`,
        {
          id: data.sub,
          first_name: data.given_name,
          last_name: data.family_name,
          photo_url: data.picture,
          username: data.name,
          email: data.email,
          auth_date: Date.now(),
          hash: "google",
          code: codeToPass, // ALWAYS SENT
        },
        { withCredentials: true }
      )
      .then((response) => {
        const token = response.data.token || response.data.user.tokens[0];

        if (token) {
          const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
          setCookie("access_token", token, { path: "/", expires });
        }

        dispatch(setSessionToken(token));
        dispatch(setUserData(response.data.user));
        dispatch(setIsLoggedIn(true));
      })
      .catch((error) => {
        console.log("Google login error:", error.response || error.message);
      })
      .finally(() => setIsLoading(false));
  };

  // Main auto-login + JWT + magic link login logic
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const magicToken = params.get("magic-login-token");
    const queryJWTToken = params.get("jwt");

    const cookieAccessToken =
      process.env.REACT_APP_ENV_MODE === "development"
        ? process.env.REACT_APP_JWT_COOKIE
        : cookies.access_token;

    const handleLoginResponse = (token: string, user: any) => {
      if (token) {
        const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
        setCookie("access_token", token, { path: "/", expires });
      }
      dispatch(setSessionToken(token));
      dispatch(setUserData(user));
      dispatch(setIsLoggedIn(true));
    };

    const doLogin = async () => {
      setIsLoading(true);

      try {
        if (magicToken) {
          // MAGIC LINK LOGIN
          const res = await axios.post(
            `${API_URL}/users/magiclink/login`,
            { token: magicToken },
            { withCredentials: true }
          );

          const token = res.data.token || res.data.user.tokens[0];
          handleLoginResponse(token, res.data.user);
        } else if (queryJWTToken) {
          // JWT LOGIN (typically from deep link)
          const res = await axios.post(
            `${API_URL}/users/jwtlogin`,
            {},
            {
              withCredentials: true,
              headers: { Authorization: `Bearer ${queryJWTToken}` },
            }
          );

          handleLoginResponse(queryJWTToken, res.data.user);
        } else if (cookieAccessToken) {
          // COOKIE LOGIN
          const res = await axios.post(
            `${API_URL}/users/login`,
            {},
            {
              withCredentials: true,
              headers: { Authorization: `Bearer ${cookieAccessToken}` },
            }
          );

          handleLoginResponse(cookieAccessToken, res.data.user);
        }
      } catch (error: any) {
        console.log("Auto-login error:", error.response || error.message);
      } finally {
        setIsLoading(false);
      }
    };

    doLogin();
  }, [API_URL, cookies, dispatch, setCookie, setIsLoading]);

  return (
    <div className={classes.login}>
      <div className="container">
        {/* Show Invite Code if Present */}
        {inviteCode && (
          <div className={classes.inviteCode}>
            {t.inviteCodeLabel}
            <div className={classes.code}>{inviteCode}</div>
          </div>
        )}

        {/* Magic Link Login */}
        <div className={classes.magicLogin}>
          <input
            type="email"
            placeholder={t.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            onClick={handleRequestMagicLink}
            small
            label={magicLinkSent ? t.magicLinkSent : t.magicLoginButton}
          />
        </div>

        {/* Google Login */}
        <GoogleOAuthProvider clientId="758181930849-pv93gkctgo5j2pnv5kir65opegcnla6p.apps.googleusercontent.com">
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <GoogleLogin
              onSuccess={handleCredentialResponse}
              onError={() => console.log(t.googleLoginFailed)}
            />
          </div>
        </GoogleOAuthProvider>
      </div>
    </div>
  );
};

export default Login;
